<script setup lang="ts">
import { inject, onMounted, ref } from 'vue'
import { useToast as usePrimeToast } from 'primevue/usetoast'
import type { PageSettings } from '~/layers/cms/app/types/cmsTypes'
import { default_page_settings, pageGroupOptions } from '~/layers/cms/app/types/cmsTypes'
import type Textarea from 'primevue/textarea'
import { getPageSettings, savePageSettings } from '~/layers/cms/app/composables/cms'
import { useChooseFile } from '~/layers/cms/app/composables/chooseFile'
import ToggleSwitch from 'primevue/toggleswitch'

const toast = usePrimeToast()
const dialogRef = inject<DialogRef>('dialogRef')
const pageSettings = ref<PageSettings>()

interface DialogRef {
  value: {
    data: {
      slug: string
    }
    close(): void
  }
}

const closeDialog = () => {
  if (!dialogRef) return
  dialogRef.value.close()
}

onMounted(async () => {
  if (!dialogRef || !dialogRef.value.data.slug) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Nie udało się pobrac ustawień strony',
      life: 3000
    })
    return
  }
  const response = await getPageSettings(dialogRef.value.data.slug)
  if (!response.status_ok) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Nie udało się pobrac ustawień strony',
      life: 3000
    })
  }

  const data = response.data as Partial<PageSettings>
  pageSettings.value = {
    ...default_page_settings,
    ...data,
    customData:
      data.customData && !Array.isArray(data.customData)
        ? data.customData
        : {}
  }
})

const saveSettings = async () => {
  if (!pageSettings.value || !dialogRef || !dialogRef.value.data.slug) {
    return
  }

  const response = await savePageSettings(dialogRef.value.data.slug, pageSettings.value)
  if (!response.status_ok) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Nie udało się zapisać ustawień strony',
      life: 3000
    })
  } else {
    toast.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Zapisano ustawienia strony',
      life: 3000
    })
    closeDialog()
  }

  pageSettings.value = undefined
}

const { chooseFile } = useChooseFile()

const selectFile = async (assign: (src: string) => void) => {
  const newSrc = await chooseFile()
  if (newSrc) assign(newSrc)
}
</script>

<template>
  <div
    v-if="pageSettings"
    class="flex flex-col gap-6"
  >
    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">SEO</span>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Meta title*</label>
        <InputText
          v-model="pageSettings.metaTitle"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Meta description</label>
        <InputText
          v-model="pageSettings.metaDescription"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Indeksowanie</label>
        <ToggleSwitch v-model="pageSettings.indexPage" />
        <span class="text-gray-500 text-xs">Indeksuj stronę</span>
      </div>

      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Favico</label>
        <div class="flex flex-col gap-2">
          <div
            v-for="item in [
              { key: 'faviconIco', label: 'favicon.ico' },
              { key: 'favico16', label: '16×16' },
              { key: 'favico32', label: '32×32' },
              { key: 'favico48', label: '48×48' },
              { key: 'favico64', label: '64×64' },
              { key: 'appleTouchIcon', label: 'Apple 180×180' },
              { key: 'favico192', label: 'Android 192×192' },
              { key: 'favico512', label: 'PWA 512×512' }
            ]"
            :key="JSON.stringify(item)"
            class="p-2 flex flex-row items-center justify-between gap-2"
          >
            <div class="flex items-center gap-2 min-w-0">
              <template v-if="pageSettings[item.key]">
                <NuxtImg
                  :src="pageSettings[item.key]"
                  class="w-6 h-6"
                  alt="icon"
                  width="24"
                  height="24"
                  loading="lazy"
                  quality="100"
                />
              </template>

              <div class="flex flex-col min-w-0">
                <span>{{ item.label }}</span>
                <span
                  class="text-xs"
                  :class="pageSettings[item.key] ? 'text-green-600' : 'text-gray-500'"
                >
                  {{ pageSettings[item.key] ? 'Ustawiono' : 'Brak' }}
                </span>
              </div>
            </div>

            <button
              class="button-admin-blue-transparent"
              @click="selectFile(src => pageSettings![item.key] = src)"
            >
              {{ pageSettings[item.key] ? 'Zmień' : 'Dodaj' }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Grupa</label>
        <Select
          v-model="pageSettings.group"
          :options="pageGroupOptions"
          class="flex-1 px-2 text-sm"
        />
      </div>
      <div
        v-if="pageSettings.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Tytuł aktualności</label>
        <InputText
          v-model="(pageSettings.customData.articleTitle as string)"
          class="flex-1 text-sm"
        />
      </div>
      <div
        v-if="pageSettings.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Opis aktualności</label>
        <InputText
          v-model="(pageSettings.customData.articleDescription as string)"
          class="flex-1 text-sm"
        />
      </div>
      <div
        v-if="pageSettings.group == 'aktualność'"
        class="flex items-center gap-3 relative"
      >
        <label class="w-32 shrink-0 text-gray-500">Data aktualności</label>
        <DatePicker
          v-model="(pageSettings.customData.articleDate as Date)"
          date-format="dd-mm-yy"
          show-icon
          show-button-bar
          :manual-input="true"
          placeholder="DD-MM-RRRR"
          class="flex-1 text-sm"
        />
      </div>
      <div
        v-if="pageSettings.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Grafika aktualności</label>
        <NuxtImg
          :src="(pageSettings.customData.articleImage as string)"
          width="400"
          height="200"
          class="object-cover w-100 h-50 min-w-100 min-h-50 cursor-pointer"
          loading="lazy"
          format="webp"
          alt="grafika aktualności"
          quality="100"
          @click="selectFile(src => { pageSettings!.customData ??= {}; pageSettings!.customData.articleImage = src })"
        />
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">Open Graph</span>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">OG title</label>
        <InputText
          v-model="pageSettings.ogTitle"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">OG description</label>
        <InputText
          v-model="pageSettings.ogDescription"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">OG image</label>
        <div
          class="w-12 h-12 border border-dashed border-gray-800 rounded flex items-center justify-center cursor-pointer overflow-hidden bg-gray-700"
          @click="selectFile(src => pageSettings!.ogImage = src)"
        >
          <NuxtImg
            v-if="pageSettings.ogImage"
            :src="pageSettings.ogImage"
            class="w-full h-full object-cover"
            width="400"
            height="400"
            format="webp"
            loading="lazy"
            alt="ogImage"
            quality="100"
          />
          <span
            v-else
            class="text-gray-800 text-xl"
          >+</span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Twitter card</label>
        <InputText
          v-model="pageSettings.twitterCard"
          class="flex-1 text-sm"
        />
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">Zaawansowane</span>
      <div class="flex items-start gap-3">
        <label class="w-32 shrink-0 text-gray-500 pt-1">Head code</label>
        <Textarea
          v-model="pageSettings.customHeadCode"
          class="flex-1 h-16 min-h-16 text-sm resize-y"
        />
      </div>
      <div class="flex items-start gap-3">
        <label class="w-32 shrink-0 text-gray-500 pt-1">Body code</label>
        <Textarea
          v-model="pageSettings.customBodyCode"
          class="flex-1 h-16 min-h-16 text-sm resize-y"
        />
      </div>
    </div>
    <Button
      type="button"
      label="Zapisz"
      severity="primary"
      @click="saveSettings"
    />
  </div>
  <div
    v-else
    class="p-8 text-center surface-ground border-round border-px surface-border"
  >
    <i class="pi pi-exclamation-circle text-2xl mb-2 block" />
    <span>Nie załadowano ustawień</span>
  </div>
</template>
